import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

export default function RegistrationSettingsForm() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchRegistrationSettings();
  }, []);

  const fetchRegistrationSettings = async () => {
    try {
      setIsLoading(true);

      const { data: settings, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .eq('key', 'registration_enabled');

      if (error) throw error;

      if (settings) {
        settings.forEach((setting) => {
          if (setting.key === 'registration_enabled') {
            const value = typeof setting.value === 'boolean'
              ? setting.value
              : setting.value === 'true' || setting.value === true;
            setIsEnabled(value);
          }
        });
      }
    } catch (error) {
      console.error('Error fetching registration settings:', error);
      toast.error('Failed to load registration settings');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRegistration = async () => {
    try {
      setIsSaving(true);
      const newValue = !isEnabled;

      const { error } = await supabase
        .from('system_settings')
        .upsert({
          key: 'registration_enabled',
          value: newValue,
          category: 'registration',
          description: 'Enable or disable player registration',
          is_public: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      setIsEnabled(newValue);
      toast.success(
        newValue
          ? 'Registration has been enabled'
          : 'Registration has been disabled'
      );
    } catch (error) {
      console.error('Error toggling registration:', error);
      toast.error('Failed to update registration status');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-golf-gray mb-2">Registration Settings</h2>
        <p className="text-sm text-gray-600">
          Control when and how players can register for tournaments
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-lg ${isEnabled ? 'bg-green-100' : 'bg-red-100'}`}>
              {isEnabled ? (
                <LockOpenIcon className="w-6 h-6 text-green-600" />
              ) : (
                <LockClosedIcon className="w-6 h-6 text-red-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-golf-gray">
                Enable/Disable Registration
              </h3>
              <p className="text-sm text-gray-600">
                {isEnabled
                  ? 'Players can currently register for the tournament'
                  : 'Registration is currently closed to new players'}
              </p>
            </div>
          </div>
          <button
            onClick={toggleRegistration}
            disabled={isSaving}
            className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 ${
              isEnabled ? 'bg-green-600' : 'bg-gray-300'
            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                isEnabled ? 'translate-x-9' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {!isEnabled && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              When registration is disabled, the registration button will be hidden and players cannot submit new registrations.
            </p>
          </div>
        )}
      </motion.div>

    </div>
  );
}
